import React, { useContext } from 'react';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { layoutForVariant } from './variants';
import { BannerImpression } from './common/BannerImpression';
import { useAskMagicApron } from '../hooks/useAskMagicApron';
import { useSuggestedQuestions } from '../hooks/useSuggestedQuestions';
import { useSearchBannerVariant } from '../hooks/useSearchBannerVariant';
import { dataModel } from '../data-model/suggestedQuestions';
import { toBannerContent } from '../utils/bannerContent';
import { decodeKeyword } from '../utils/decodeKeyword';

/**
 * Fetches the banner's content and hands it to whichever layout the Config Service switch selects.
 *
 * Every variant receives the same props and the same behaviour, so this component holds no layout
 * knowledge beyond the registry lookup — a variant can be added or retired without touching it.
 */
const AiAssistedSearchBanner = () => {
  const variant = useSearchBannerVariant();
  const Layout = layoutForVariant(variant);
  const isDisabled = !Layout;

  const { customContext, customer } = useContext(ExperienceContext) || {};

  const anchor = decodeKeyword(customContext?.variables?.keyword || '');

  const askQuestion = useAskMagicApron({
    entryLocationForAnalytics: `ai-assisted-search-banner-${variant}`,
    enabled: !isDisabled
  });

  // A blank anchor skips the request, so a disabled switch costs the API nothing.
  const { data, loading, error } = useSuggestedQuestions({
    anchor: isDisabled ? '' : anchor,
    customerType: customer?.type
  });

  if (isDisabled || loading || error || !data) {
    return null;
  }

  const { questions, summary } = toBannerContent(data, anchor);

  if (!questions.length && !summary) {
    return null;
  }

  return (
    <BannerImpression anchor={anchor} variant={variant}>
      <Layout
        questions={questions}
        summary={summary}
        onAskQuestion={askQuestion}
      />
    </BannerImpression>
  );
};

AiAssistedSearchBanner.displayName = 'AiAssistedSearchBanner';

AiAssistedSearchBanner.dataModel = dataModel;

export { AiAssistedSearchBanner };
